export const JOB_TITLE = "Chief of Everything That Isn't Bookings";

const Section = ({ n, eyebrow, title, children, tone = "plain" }) => (
  <section
    className={tone === "card" ? "bg-[#F4ECE2] rounded-xl sm:rounded-2xl border border-stone-100 p-5 sm:p-7" : ""}
    data-testid={`job-section-${n}`}
  >
    <p className="text-[#C85A38] font-medium text-xs sm:text-sm mb-1">{eyebrow}</p>
    <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-4">{title}</h2>
    <div className="text-sm sm:text-base text-[#2E2A26]/80 leading-relaxed flex flex-col gap-3">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="flex flex-col gap-2.5">
    {items.map((t) => (
      <li key={t} className="flex gap-3">
        <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-[#C85A38] flex-shrink-0" aria-hidden="true" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const glance = [
  ["Where", "Sierra Calderona, 25 minutes from Valencia"],
  ["Hours", "None set. It's a lifestyle job, not a shift"],
  ["Pay", "Salary, plus your own room and all food"],
  ["Start", "Immediate"],
  ["Term", "Long term, after a paid three-month trial"],
  ["Dogs", "Three"],
];

export const JobGlance = () => (
  <aside className="bg-[#E8DAC8] rounded-xl sm:rounded-2xl p-5 sm:p-6" data-testid="job-glance">
    <p className="text-[#C85A38] font-medium text-xs sm:text-sm mb-3">At a glance</p>
    <dl className="flex flex-col gap-2.5">
      {glance.map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-2 text-sm">
          <dt className="text-[#2E2A26] font-medium flex-shrink-0">{k}</dt>
          <span className="flex-1 border-b border-dotted border-[#2E2A26]/30 translate-y-[-4px]" aria-hidden="true" />
          <dd className="text-[#2E2A26]/75 text-right max-w-[62%]">{v}</dd>
        </div>
      ))}
    </dl>
  </aside>
);

export const JobPost = () => (
  <article className="flex flex-col gap-10 sm:gap-12" data-testid="job-post">
    <div className="text-sm sm:text-base text-[#2E2A26]/80 leading-relaxed flex flex-col gap-4">
      <p>
        We've just been named Best Destination Coliving at the Coliving Awards 2026. This is lovely, and also means people now expect things to be good all the time. That's where you come in.
      </p>
      <p>
        Savi is a sober coliving in a large villa in the mountains, 25 minutes from Valencia. There's a pool, a gym, a yoga studio, mountain views, sixteen rooms of interesting humans and three dogs who think they run the place. They don't. Hopefully you will.
      </p>
    </div>

    <Section n={1} eyebrow="The role" title="What you'll actually do">
      <Bullets
        items={[
          "Keep the house running and looking like the brochure.",
          "Lead the volunteer crew, which means rotas, training, morale, and occasionally finding out who left the oat milk out.",
          "Look after the community: dinners, rituals, the person having a wobbly week, the person who wants to start a sourdough club.",
          "Spot what's broken before a guest does.",
          "Have ideas and then make them happen, which is the rarer of the two.",
        ]}
      />
      <p>You won't be doing bookings (hence the title) or accounts. Someone else enjoys that. Nobody's sure why.</p>
      <p data-testid="job-lifestyle">
        A word on hours: there aren't set ones. This is a lifestyle job. You live here, the house is your house, and the work is whatever the day brings. Some days that's a lot. Some days it's a swim.
      </p>
    </Section>

    <Section n={2} eyebrow="The person" title="Who you are">
      <p>
        Someone who notices things. You can lead a team without turning into a clipboard. You're calm when the pool pump dies on a Sunday, you're good with people of all kinds, and you're comfortable living and working in a sober environment.
      </p>
      <p>A sense of humour isn't required, but you'll suffer without one.</p>
    </Section>

    <Section n={3} eyebrow="The terms" title="The deal" tone="card">
      <p>
        It's a paid three-month trial. Think of it as a career change you can test drive. If it's working, and you're the right person, there's a real path to becoming a partner in the Savi brand as it grows.
      </p>
      <p>That's not a vague promise to get you to clean the gutters. Although there are gutters.</p>
      <p data-testid="job-long-haul">
        One thing worth saying plainly. Savi is growing, and the award has only sped that up. The three months are how we both check it's right, not how long we want you for. We're after someone who's along for the ride.
      </p>
    </Section>

    <Section n={4} eyebrow="Next step" title="How to apply">
      <p>
        Fill in the form below. Tell us who you are, why this, and the most useful thing you've ever fixed. Bonus points for brevity. Minus points for the word 'synergy'.
      </p>
    </Section>
  </article>
);
